/**
 * Part B UI (SPEC v0.9.3) — "บ้านคุณอยู่ท้ายเขื่อน / เหนือเขื่อน <ชื่อ>" + 7-day rain for
 * the dam's catchment (เหนือเขื่อน) and tailwater (ท้ายเขื่อน), from /api/dam-rain. Tapping a
 * day shows the B4 wording for that day (moderate+ rain only) and colours the two zones on
 * the map with a dashed divider at the dam (B3). No data → nothing shown. No mock; every
 * number comes from the API. Wording is EXACTLY the approved B4 phrases — no other words.
 */
import { placeWord } from "./place-word";
import maplibregl, { type Map as MlMap, type GeoJSONSource } from "maplibre-gl";
import { thaiToday, thaiTomorrow } from "@flowsight/sim";

export interface DamZoneDay { date: string; dow: string; m: number; min: number; max: number; median: number; level: string; heavyCount: number; veryHeavyCount: number; isHeavyDay: boolean; confidence: string }
interface Zone { days: DamZoneDay[]; points: [number, number][] }
export interface DamRainData { relation: "below" | "above" | null; rainUnavailable?: boolean; dam: string; alongKm: number; damLngLat: [number, number]; damPercent: number | null; up: Zone | null; down: Zone | null; updatedAt: string; source: string }

const LEVEL: Record<string, { c: string; th: string }> = {
  none: { c: "#4a5a6e", th: "ไม่มีฝน" }, light: { c: "#4aa3df", th: "ฝนเล็กน้อย" }, moderate: { c: "#2ec7b8", th: "ฝนปานกลาง" },
  heavy: { c: "#ffb020", th: "ฝนหนัก" }, veryheavy: { c: "#ff3b5c", th: "ฝนหนักมาก" },
};
const RANK: Record<string, number> = { none: 0, light: 1, moderate: 2, heavy: 3, veryheavy: 4 };
const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));
const dayNum = (iso: string): number => Number(iso.slice(8, 10));
function dLabel(d: DamZoneDay): string {
  const now = new Date();
  if (d.date === thaiToday(now)) return "วันนี้";
  if (d.date === thaiTomorrow(now)) return "พรุ่งนี้";
  return `${d.dow}${dayNum(d.date)}`;
}

/** null = no related dam · data with up/down null + rainUnavailable = related dam, rain failed ·
 *  throws on a network/API error. One request per point (memoised: the card, the Part D
 *  advice and the summary share it). */
const damRainMemo = new Map<string, Promise<DamRainData | null>>();
export function loadDamRainOrThrow(lat: number, lon: number): Promise<DamRainData | null> {
  const key = `${lat.toFixed(4)},${lon.toFixed(4)}`;
  let p = damRainMemo.get(key);
  if (!p) {
    p = (async () => {
      const r = await fetch(`/api/dam-rain?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`);
      if (!r.ok) throw new Error(`dam-rain ${r.status}`);
      const d = (await r.json()) as DamRainData;
      return d.relation ? d : null;
    })();
    damRainMemo.set(key, p);
    p.then((d) => { if (d?.rainUnavailable) damRainMemo.delete(key); }, () => damRainMemo.delete(key)); // retry later, don't pin a failure
    if (damRainMemo.size > 20) damRainMemo.delete(damRainMemo.keys().next().value!);
  }
  return p;
}
/** For the rain card / advice: a related dam WITH rain, else null. */
export async function loadDamRain(lat: number, lon: number): Promise<DamRainData | null> {
  try { const d = await loadDamRainOrThrow(lat, lon); return d && (d.up || d.down) ? d : null; } catch { return null; }
}

/** The approved B4 lines for a zone on a given day — shown only for moderate+ rain. */
export function b4ForDay(data: DamRainData, i: number): string[] {
  const out: string[] = [];
  const up = data.up?.days[i], down = data.down?.days[i];
  if (up && (RANK[up.level] ?? 0) >= 2) {
    const pct = data.damPercent; // X% from real dam data; null → drop the parenthetical, never guess
    if (pct != null && pct >= 80) out.push(`ฝนตกเหนือเขื่อน เขื่อนเกือบเต็ม (${pct}%) อาจต้องระบายเพิ่ม`);
    else if (pct != null) out.push(`ฝนตกเหนือเขื่อน น้ำไหลลงอ่างก่อน เขื่อนยังรับได้ (น้ำ ${pct}%)`);
    else out.push(`ฝนตกเหนือเขื่อน น้ำไหลลงอ่างก่อน`); // no % → direction only, no capacity claim
  }
  if (down && (RANK[down.level] ?? 0) >= 2) out.push(`ฝนตกท้ายเขื่อน น้ำไหลลงแม่น้ำโดยตรง เขื่อนกักไว้ไม่ได้`);
  return out;
}

function zoneRow(title: string, zone: Zone | null, sel: number, maxMm: number): string {
  if (!zone) return "";
  const bars = zone.days.map((d, i) => {
    const col = LEVEL[d.level]?.c ?? LEVEL.none!.c;
    const h = Math.round((d.median / maxMm) * 22) + 2;
    return `<button class="dr-bar-btn${i === sel ? " sel" : ""}" data-i="${i}" title="${dLabel(d)}: ${d.min}–${d.max} มม.">
      <span class="dr-bar" style="height:${h}px;background:${col}"></span><span class="dr-bl">${dLabel(d)}</span></button>`;
  }).join("");
  const d = zone.days[sel]!;
  const range = d.min === d.max ? `${d.median} มม.` : `${d.min}–${d.max} มม.`;
  return `<div class="dr-zone"><div class="dr-zone-h">${title} · <span style="color:${LEVEL[d.level]?.c}">${LEVEL[d.level]?.th}</span> <span class="muted">${range} · ${d.heavyCount === 0 ? `ทั้ง ${d.m} แบบจำลองเห็นตรงกันว่าไม่มีฝนหนัก` : `${d.heavyCount} จาก ${d.m} เห็นฝนหนัก`}</span></div><div class="dr-bars">${bars}</div></div>`;
}

export function damRainCard(data: DamRainData, onDay: (i: number) => void): HTMLElement {
  const card = document.createElement("div");
  card.className = "dr-card";
  const head = data.relation === "below" ? `${placeWord()}อยู่ท้ายเขื่อน${data.dam.replace(/^เขื่อน/, "")}` : `${placeWord()}อยู่เหนือเขื่อน${data.dam.replace(/^เขื่อน/, "")}`;
  const maxMm = Math.max(1, ...(data.up?.days ?? []).map((d) => d.max), ...(data.down?.days ?? []).map((d) => d.max));
  let sel = 0;
  // default to the first day either zone is moderate+ (where B4 has something to say)
  const n = data.up?.days.length ?? data.down?.days.length ?? 0;
  for (let i = 0; i < n; i++) if (b4ForDay(data, i).length) { sel = i; break; }

  const render = (): void => {
    const b4 = b4ForDay(data, sel);
    card.innerHTML = `<div class="dr-head">📍 ${esc(head)}</div>
      ${zoneRow("เหนือเขื่อน (พื้นที่รับน้ำ)", data.up, sel, maxMm)}
      ${zoneRow("ท้ายเขื่อน", data.down, sel, maxMm)}
      ${b4.length ? `<div class="dr-b4">${b4.map((t) => `<div class="dr-b4-line">• ${esc(t)}</div>`).join("")}</div>` : `<div class="muted small dr-b4">วันนี้ไม่มีฝนปานกลางขึ้นไปในโซนเขื่อนนี้</div>`}
      <div class="muted small" style="margin-top:6px">${esc(data.source)} · อัปเดต ${new Date(data.updatedAt).toLocaleString("th-TH", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>`;
    card.querySelectorAll<HTMLElement>(".dr-bar-btn").forEach((b) => b.addEventListener("click", () => { sel = Number(b.dataset.i); render(); onDay(sel); }));
  };
  render();
  // B3 colours the map only when the user taps a day (avoids yanking the map off the
  // user's radius as the card loads). The card shows the zone bars + B4 text immediately.
  return card;
}

/* ── B3: colour the two zones on the map + dashed divider at the dam ─────────── */
const SRC = "dr-zones-src", DIV = "dr-div-src", USR = "dr-user-src";
let savedView: { center: [number, number]; zoom: number } | null = null; // view before a day was tapped
let lastIdx = -1;

export function installDamZoneLayers(map: MlMap): void {
  if (map.getSource(SRC)) return;
  map.addSource(SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  map.addSource(DIV, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  map.addSource(USR, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  map.addLayer({ id: "dr-zones-fill", type: "circle", source: SRC, paint: {
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 10, 9, 26],
    "circle-color": ["get", "color"], "circle-opacity": 0.28, "circle-blur": 0.6,
    "circle-stroke-color": ["get", "color"], "circle-stroke-width": 1.2, "circle-stroke-opacity": 0.7,
  } });
  map.addLayer({ id: "dr-div-line", type: "line", source: DIV, paint: { "line-color": "#e8edf5", "line-width": 2, "line-dasharray": [2, 2] } });
  map.addLayer({ id: "dr-div-dot", type: "circle", source: DIV, filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 5, "circle-color": "#e8edf5", "circle-stroke-color": "#05080f", "circle-stroke-width": 2 } });
  // prominent "บ้านคุณ" / "จุดนี้" marker so the user sees where home is relative to the dam (item 2c)
  map.addLayer({ id: "dr-user-dot", type: "circle", source: USR, paint: { "circle-radius": 7, "circle-color": "#00e5ff", "circle-stroke-color": "#05080f", "circle-stroke-width": 2.5 } });
  map.addLayer({ id: "dr-user-label", type: "symbol", source: USR, layout: { "text-field": "📍 จุดนี้", "text-font": ["Noto Sans Regular"], "text-size": 12, "text-offset": [0, 1.3], "text-anchor": "top", "text-optional": true }, paint: { "text-color": "#8fefff", "text-halo-color": "#05080f", "text-halo-width": 1.6 } });
}

export function damZonesActive(): boolean { return savedView != null; }
/** Restore the view from before a day was tapped (used by 💧 and same-day re-tap, item 2b). */
export function restoreDamView(map: MlMap): void { if (savedView) { map.easeTo({ center: savedView.center, zoom: savedView.zoom, duration: 500 }); savedView = null; lastIdx = -1; } }

function ringLine(lon: number, lat: number, km: number): GeoJSON.Feature {
  const pts: [number, number][] = [];
  const dLat = km / 111, dLon = km / (111 * Math.cos(lat * Math.PI / 180));
  for (let a = 0; a <= 360; a += 15) { const r = a * Math.PI / 180; pts.push([lon + dLon * Math.cos(r), lat + dLat * Math.sin(r)]); }
  return { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: pts } };
}

export function showDamZones(map: MlMap, data: DamRainData, i: number, userPt?: { lon: number; lat: number }): void {
  installDamZoneLayers(map);
  // item 2b: tapping the SAME day again → restore the view from before and stop
  if (i === lastIdx && savedView) { restoreDamView(map); return; }
  if (savedView == null) savedView = { center: [map.getCenter().lng, map.getCenter().lat], zoom: map.getZoom() };
  lastIdx = i;

  const feats: GeoJSON.Feature[] = [];
  const add = (zone: Zone | null): void => {
    if (!zone) return;
    const col = LEVEL[zone.days[i]?.level ?? "none"]?.c ?? LEVEL.none!.c;
    for (const [lon, lat] of zone.points) feats.push({ type: "Feature", properties: { color: col }, geometry: { type: "Point", coordinates: [lon, lat] } });
  };
  add(data.up); add(data.down);
  (map.getSource(SRC) as GeoJSONSource).setData({ type: "FeatureCollection", features: feats });
  (map.getSource(DIV) as GeoJSONSource).setData({ type: "FeatureCollection", features: [ringLine(data.damLngLat[0], data.damLngLat[1], 6), { type: "Feature", properties: {}, geometry: { type: "Point", coordinates: data.damLngLat } }] });
  // item 2c: show the user's home marker
  const uSrc = map.getSource(USR) as GeoJSONSource | undefined;
  if (map.getLayer("dr-user-label")) map.setLayoutProperty("dr-user-label", "text-field", `📍 ${placeWord()}`); // v0.9.9a
  if (uSrc) uSrc.setData({ type: "FeatureCollection", features: userPt ? [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [userPt.lon, userPt.lat] } }] : [] });
  // item 2a: frame the dam + BOTH zones + the user's point in one view
  const all: [number, number][] = [data.damLngLat, ...(data.up?.points ?? []), ...(data.down?.points ?? [])];
  if (userPt) all.push([userPt.lon, userPt.lat]);
  const b = new maplibregl.LngLatBounds(all[0]!, all[0]!);
  for (const p of all) b.extend(p);
  map.fitBounds(b, { padding: { top: 70, bottom: 280, left: 48, right: 48 }, maxZoom: 9, duration: 600 });
}

export function clearDamZones(map: MlMap): void {
  for (const id of [SRC, DIV, USR]) (map.getSource(id) as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: [] });
  savedView = null; lastIdx = -1;
}
